/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Create System Wizard
 * A guided, step-by-step flow for creating a new AI System.
 * Steps: Template → Name & Purpose → Settings → Launch
 */

import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  SmartToy as AIIcon,
  Build as ManualIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapIcon,
  Shield as ShieldIcon,
  Code as CodeIcon,
  Dashboard as DashboardIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { aiSystemApi } from '../../services/api';
import type { AISystemCreate } from '../../types';

// ───────────────── Templates ─────────────────

interface SystemTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  defaultName: string;
  defaultDescription: string;
  tags: string[];
}

const SYSTEM_TEMPLATES: SystemTemplate[] = [
  {
    id: 'day-trader',
    title: 'Day Trading System',
    description: 'Multi-expert workflow for intraday entries and exits with risk management.',
    icon: <TrendingUpIcon />,
    color: '#4caf50',
    defaultName: 'Day Trading System',
    defaultDescription: 'Analyzes higher timeframe bias, lower timeframe entries, calculates risk/position sizing, and executes trades.',
    tags: ['Trading', 'Intraday'],
  },
  {
    id: 'swing-analyzer',
    title: 'Swing Analysis System',
    description: 'Identify swing opportunities using fundamental and technical analysis.',
    icon: <SwapIcon />,
    color: '#2196f3',
    defaultName: 'Swing Analysis System',
    defaultDescription: 'Combines fundamental context with multi-timeframe technical analysis for swing trade setups.',
    tags: ['Analysis', 'Swing'],
  },
  {
    id: 'risk-monitor',
    title: 'Risk Monitor',
    description: 'Continuously monitor open positions and alert on risk threshold breaches.',
    icon: <ShieldIcon />,
    color: '#ff9800',
    defaultName: 'Risk Monitor',
    defaultDescription: 'Monitors account exposure, drawdown, and correlation risk across open positions.',
    tags: ['Risk', 'Monitoring'],
  },
  {
    id: 'code-pipeline',
    title: 'Data Pipeline',
    description: 'Chain code experts to fetch, transform, and analyze market data programmatically.',
    icon: <CodeIcon />,
    color: '#9c27b0',
    defaultName: 'Data Pipeline',
    defaultDescription: 'A code-driven pipeline that fetches data, applies transformations, and produces structured output.',
    tags: ['Code', 'Data'],
  },
  {
    id: 'custom',
    title: 'Blank System',
    description: 'Start from scratch and build your own workflow.',
    icon: <DashboardIcon />,
    color: '#607d8b',
    defaultName: '',
    defaultDescription: '',
    tags: ['Custom'],
  },
];

// ───────────────── Wizard Steps ─────────────────

const STEPS = ['Template', 'Name & Purpose', 'Settings', 'Launch'];

// ───────────────── Component ─────────────────

interface CreateSystemWizardProps {
  open: boolean;
  onClose: () => void;
}

export const CreateSystemWizard: React.FC<CreateSystemWizardProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);

  // Derived
  const template = useMemo(
    () => SYSTEM_TEMPLATES.find((t) => t.id === selectedTemplate),
    [selectedTemplate],
  );

  // Reset on open
  const handleClose = () => {
    setActiveStep(0);
    setSelectedTemplate(null);
    setName('');
    setDescription('');
    setIsActive(false);
    setRequireApproval(true);
    onClose();
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AISystemCreate) => aiSystemApi.create(data),
    onSuccess: (createdSystem) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      toast.success('System created! Opening the builder…');
      handleClose();
      navigate(`/ai/systems/${createdSystem.id}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to create system');
    },
  });

  // Validation per step
  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return selectedTemplate !== null;
      case 1:
        return name.trim().length > 0;
      case 2:
        return true; // settings are optional
      case 3:
        return true;
      default:
        return false;
    }
  }, [activeStep, selectedTemplate, name]);

  // Navigation
  const handleNext = () => {
    if (activeStep === 0 && template) {
      // Prefill from template
      if (!name && template.defaultName) setName(template.defaultName);
      if (!description && template.defaultDescription) setDescription(template.defaultDescription);
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Launch handlers
  const handleLaunch = (mode: 'manual' | 'ai') => {
    createMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      is_active: isActive,
      config: {
        require_approval: requireApproval,
        created_from_template: selectedTemplate,
        initial_builder_mode: mode,
      },
    });
  };

  // ───────────── Step Renderers ─────────────

  const renderTemplateStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        What are you building?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pick a starting point. You can always change everything later.
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
        gap={2}
      >
        {SYSTEM_TEMPLATES.map((tpl) => {
          const isSelected = selectedTemplate === tpl.id;
          return (
            <Paper
              key={tpl.id}
              variant="outlined"
              onClick={() => setSelectedTemplate(tpl.id)}
              sx={{
                p: 2.5,
                cursor: 'pointer',
                borderWidth: 2,
                borderColor: isSelected ? tpl.color : 'divider',
                bgcolor: isSelected ? `${tpl.color}12` : 'background.paper',
                transition: 'all 0.15s',
                '&:hover': {
                  borderColor: tpl.color,
                  bgcolor: `${tpl.color}08`,
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${tpl.color}20`,
                  color: tpl.color,
                  mb: 1.5,
                }}
              >
                {tpl.icon}
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {tpl.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}>
                {tpl.description}
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {tpl.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );

  const renderNameStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        Name & purpose
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Give it a clear name so you can find it later. The description helps the AI assistant understand what you're building.
      </Typography>

      <Stack spacing={3} sx={{ maxWidth: 560 }}>
        <TextField
          label="System name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          autoFocus
          error={activeStep === 1 && !name.trim()}
          helperText={!name.trim() ? 'Required — e.g. "GBP/USD Day Trader"' : ''}
          placeholder="e.g. Day Trading GBP/USD"
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={3}
          maxRows={5}
          fullWidth
          placeholder="What does this system do? What markets, timeframes, or strategies does it cover?"
        />
        {template && template.id !== 'custom' && (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Pre-filled from the <strong>{template.title}</strong> template. Edit freely.
          </Alert>
        )}
      </Stack>
    </Box>
  );

  const renderSettingsStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        Key settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These can always be changed from the gear icon in the builder.
      </Typography>

      <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Active</Typography>
                <Typography variant="caption" color="text.secondary">
                  When active, scheduled runs will fire. Leave off until the system is ready.
                </Typography>
              </Box>
            }
            labelPlacement="start"
          />
        </Paper>

        <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            control={<Switch checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Trade Approval</Typography>
                <Typography variant="caption" color="text.secondary">
                  Require manual approval before action experts can place trades.
                </Typography>
              </Box>
            }
            labelPlacement="start"
          />
        </Paper>

        <Paper variant="outlined" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Budget</Typography>
          <Typography variant="caption" color="text.disabled">
            Budget limits coming soon — set max daily/monthly credit usage per system.
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );

  const renderLaunchStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
        How do you want to build it?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Both open the same builder. You can switch between manual and AI at any time.
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
        gap={2.5}
        sx={{ maxWidth: 640 }}
      >
        {/* AI-first */}
        <Paper
          variant="outlined"
          onClick={() => handleLaunch('ai')}
          sx={{
            p: 3,
            cursor: createMutation.isPending ? 'wait' : 'pointer',
            borderWidth: 2,
            borderColor: 'primary.main',
            textAlign: 'center',
            transition: 'all 0.15s',
            '&:hover': { bgcolor: 'primary.50', boxShadow: 2 },
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AIIcon fontSize="large" />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Start with AI
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Describe what you need and the AI will add experts, wire connections, and configure the workflow for you.
          </Typography>
          <Chip label="Recommended" size="small" color="primary" sx={{ mt: 2, fontWeight: 600 }} />
        </Paper>

        {/* Manual */}
        <Paper
          variant="outlined"
          onClick={() => handleLaunch('manual')}
          sx={{
            p: 3,
            cursor: createMutation.isPending ? 'wait' : 'pointer',
            borderWidth: 2,
            borderColor: 'divider',
            textAlign: 'center',
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover', boxShadow: 2 },
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.selected',
              color: 'text.primary',
              mx: 'auto',
              mb: 2,
            }}
          >
            <ManualIcon fontSize="large" />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Build manually
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Open the expert library, drag experts onto the canvas, and wire them up yourself.
          </Typography>
          <Chip label="Full control" size="small" variant="outlined" sx={{ mt: 2 }} />
        </Paper>
      </Box>

      {createMutation.isPending && (
        <Box display="flex" alignItems="center" gap={1} mt={3}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Creating system…</Typography>
        </Box>
      )}
    </Box>
  );

  // Step content map
  const stepContent = [renderTemplateStep, renderNameStep, renderSettingsStep, renderLaunchStep];

  // ───────────── Main Dialog ─────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100%' : 520,
          maxHeight: isMobile ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          New AI System
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1, flexShrink: 0 }}>
        <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{isMobile ? '' : label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
        {stepContent[activeStep]()}
      </Box>

      {/* Footer — hidden on launch step (buttons are inline) */}
      {activeStep < STEPS.length - 1 && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<BackIcon />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed}
            endIcon={<NextIcon />}
          >
            Next
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default CreateSystemWizard;
